import { useItemModifierGroups } from 'src/hooks/useItemModifierGroups';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { flattenPagesToModifiers, generateModifierFormDefaults, mapModifierGroupToTableRow } from './utils';
import { BundleIcon, Paper, SearchField, Spinner, Table, Typography } from '@weareopenr/component-library';
import { InfiniteData } from '@tanstack/react-query';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { ItemModifierGroupsResponseBody } from 'src/api/getModifierGroups';
import { optionGroupCols } from './Table';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';

import {
  BUNDLE_MODIFIER_DESCRIPTION,
  BUNDLE_MODIFIER_FORM_ID,
  BUNDLE_MODIFIER_TITLE,
  CREATE_BUNDLE_OR_MODIFIER_FIRST,
  GO_TO_PRODUCTS,
} from './constants';
import styles from './PriceSchemeOptionGroupOverrides.module.scss';
import EmptyState from '../EmptyState';
import { useMenus } from 'src/hooks/useMenus';
import { emptySectionTextFilter } from '../PriceSchemePricingTab/constants';
import { Routes } from 'src/routes';
import { usePricingCategoryFilter } from 'src/hooks/usePricingCategoryFilter';
import { PriceSchemeFilters } from '../PriceSchemeFilters/PriceSchemeFilters';
import { PricingSavePortal } from '../PricingSavePortal';
import { useSavePriceOverrides } from './useSavePriceOverrides';

const ProductOptionGroupOverrides = ({ priceSchemeId }: { priceSchemeId: string }) => {
  const [searchMenus, setSearchMenus] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedSearchValue = useDebounce(searchValue);
  const {
    searchPricingCategories,
    setSearchPricingCategories,
    hasPricingCategoryFilterFeature,
    pricingCategoryOptions,
  } = usePricingCategoryFilter(priceSchemeId);

  const onNewProducts = useCallback(({ pages }: InfiniteData<ItemModifierGroupsResponseBody>) => {
    const flattenedPages = flattenPagesToModifiers(pages);
    const formValues = generateModifierFormDefaults(flattenedPages);
    reset(formValues, { keepDirtyValues: true, keepErrors: true });
  }, []);

  const { data: menus } = useMenus();

  const { data, isLoading, queryKey, isFetchingNextPage, fetchStatus, fetchNextPage, hasNextPage } =
    useItemModifierGroups({
      priceSchemeId,
      menus: searchMenus,
      pricingCategories: searchPricingCategories,
      name: debouncedSearchValue,
      options: {
        onSuccess: onNewProducts,
      },
    });

  const pages = data?.pages?.flatMap(({ data }) => data) ?? [];
  const tableData = pages.map(mapModifierGroupToTableRow);
  const modifiers = pages.flatMap((page) => page.modifier_groups).flatMap((p) => p.modifiers) ?? [];
  const defaultValues = generateModifierFormDefaults(modifiers);

  const { control, handleSubmit, reset, setValue } = useForm<FieldValues>({
    defaultValues,
    values: defaultValues,
  });

  const navigate = useNavigate();

  const showFilterNoResults = modifiers?.length === 0 && (searchMenus.length > 0 || searchPricingCategories.length > 0);
  const emptyStateText = showFilterNoResults ? emptySectionTextFilter : CREATE_BUNDLE_OR_MODIFIER_FIRST;

  const columns = useMemo(() => optionGroupCols({ control, setValue }), [control, setValue, fetchStatus]);

  const submitValues = useSavePriceOverrides({ priceSchemeId, queryKey, control, reset });

  if (isLoading) {
    return (
      <SpinnerContainer data-testid="loadingIcon">
        <Spinner size="xlarge" colour="rainbow" />
      </SpinnerContainer>
    );
  }

  return (
    <div className={styles['container']}>
      <div className={styles['controls-row']}>
        <div className={styles['search-filter-container']}>
          <SearchField
            key="bundle-modifier-search"
            isDisabled={false}
            placeholder="Search"
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
          />
          <PriceSchemeFilters
            menus={menus}
            searchMenus={searchMenus}
            setSearchMenus={setSearchMenus}
            hasPricingCategoryFilterFeature={hasPricingCategoryFilterFeature}
            pricingCategoryOptions={pricingCategoryOptions}
            searchPricingCategories={searchPricingCategories}
            setSearchPricingCategories={setSearchPricingCategories}
          />
        </div>
        <PricingSavePortal control={control} reset={reset} form={BUNDLE_MODIFIER_FORM_ID} />
      </div>

      <form id={BUNDLE_MODIFIER_FORM_ID} onSubmit={handleSubmit(submitValues)} className={styles['rules-list']}>
        {tableData.length > 0 ? (
          <Table
            className={styles['table-fill-height']}
            data={tableData}
            columns={columns}
            columnHeadersToRightAlign={['adjustmentValue', 'roundingValue', 'actions']}
            getSubRows={(row) => row.modifiers}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
          />
        ) : (
          <Paper className={styles['empty-state-paper']}>
            <Typography size="xlarge" weight="semi-bold">
              {BUNDLE_MODIFIER_TITLE}
            </Typography>
            <Typography className={styles['description']}>{BUNDLE_MODIFIER_DESCRIPTION}</Typography>
            <EmptyState
              emptySectionText={emptyStateText}
              {...(!showFilterNoResults && {
                buttonText: GO_TO_PRODUCTS,
                onClick: () => navigate(Routes.PRODUCT_LIBRARY),
              })}
              Icon={BundleIcon}
            />
          </Paper>
        )}
      </form>
    </div>
  );
};

export default ProductOptionGroupOverrides;
